import { useCallback } from 'react';
import { toast } from 'sonner';
import contactService from '@/services/contact/contactService';
import { getTotalRecords, getRows } from '@/utils'; //FetchHelpers

const toContactOptions = (rows) => rows.map(contact => ({
  label: `${contact.first_name} ${contact.last_name}`,
  value: contact.id.toString(),
  id: contact.id.toString(),
  email: contact.email,
  business_name: contact.business_name,
  contact_type: contact.contact_type?.name,
  state: contact?.address?.[0]?.state,
  speciality: contact.speciality,
  fullContact: contact,
  original: contact,
}));


const useContactOptions = ({ defaultPageSize = 20, filterByIsUnsubscribed = false, filterByEmail = false } = {}) => {
  const loadOptions = useCallback(async ({ search = '', page = 1, pageSize = defaultPageSize, ...filters } = {}) => {
    try {
      const params = { page, pageSize, search, ...filters };
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined || params[key] === null || params[key] === '') delete params[key];
      });
      const response = await contactService.getAllContacts(params);
      const status = typeof response?.status === 'boolean' ? response.status : true;
      if (!status) return { options: [], hasMore: false };

      let rows = getRows(response);
      if (filterByIsUnsubscribed) rows = rows.filter(c => c.email && c.is_unsubscribe === 0);
      if (filterByEmail) rows = rows.filter(c => c.email);
      const options = toContactOptions(rows);

      const totalRecords = getTotalRecords(response);
      const hasMore = typeof totalRecords === 'number'
        ? page * pageSize < totalRecords
        : options.length === pageSize;

      return { options, hasMore };
    } catch (error) {
      console.error('Error', error);
      toast.error('Failed to fetch contacts.', { description: 'Please try again later.' });
      return { options: [], hasMore: false };
    }
  }, [defaultPageSize]);

  return loadOptions;
};

const useContactLookup = ({ defaultPageSize = 20, maxPages = 50 } = {}) => {
  const fetchAll = useCallback(async ({ search = '' } = {}) => {
    const allRows = [];
    const seenIds = new Set();
    let page = 1;
    let hasMore = true;

    while (hasMore && page <= maxPages) {
      try {
        const response = await contactService.getAllContacts({ page, pageSize: defaultPageSize, search });
        const status = typeof response?.status === 'boolean' ? response.status : true;
        if (!status) break;

        const rows = getRows(response);
        if (!rows.length) break;

        let added = 0;
        rows.forEach((row) => {
          const key = row?.id !== undefined && row?.id !== null ? String(row.id) : null;
          if (key && !seenIds.has(key)) {
            seenIds.add(key);
            allRows.push(row);
            added += 1;
          }
        });

        const totalRecords = getTotalRecords(response);
        hasMore = typeof totalRecords === 'number'
          ? page * defaultPageSize < totalRecords
          : false;

        if (added === 0) break;
        page += 1;
      } catch (error) {
        console.error('Error', error);
        toast.error('Failed to fetch contacts.', { description: 'Please try again later.' });
        break;
      }
    }

    return toContactOptions(allRows);
  }, [defaultPageSize, maxPages]);

  return fetchAll;
};

export { useContactOptions, useContactLookup };
